import React, { useState, useEffect, useRef } from 'react';
import { collateralAPI, projectAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../../components/ui/dropdown-menu';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { 
  FileText, Image as ImageIcon, Video, File, Plus, Search, Trash2,
  Eye, Folder, FolderPlus, Upload, Grid, List, MoreVertical,
  ChevronRight, FolderOpen, X, Edit2, Move, ExternalLink, Download,
  Filter, FileImage, Presentation, Building2
} from 'lucide-react';

const CollateralsPage = () => {
  const { hasRole } = useAuth();
  const fileInputRef = useRef(null);
  
  const [collaterals, setCollaterals] = useState([]);
  const [folders, setFolders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({ total: 0, folders: 0, by_type: {} });
  const [loading, setLoading] = useState(true);
  
  // View state
  const [viewMode, setViewMode] = useState('grid');
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  
  // Dialogs
  const [uploadDialog, setUploadDialog] = useState(false);
  const [folderDialog, setFolderDialog] = useState({ open: false, folder: null, isEdit: false });
  const [moveDialog, setMoveDialog] = useState({ open: false, collateral: null });
  const [previewDialog, setPreviewDialog] = useState({ open: false, collateral: null });
  
  // Forms
  const [newCollateral, setNewCollateral] = useState({
    title: '', file_type: 'document', url: '', thumbnail_url: '', project_id: '', folder_id: null, tags: []
  });
  const [newFolder, setNewFolder] = useState({ name: '', description: '', color: '#3b82f6' });
  
  const canManage = hasRole(['builder_admin', 'marketing_team']);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadCollaterals();
  }, [selectedFolder, typeFilter, projectFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [foldersRes, projectsRes, statsRes] = await Promise.all([
        collateralAPI.listFolders(),
        projectAPI.list(),
        collateralAPI.getStats()
      ]);
      setFolders(foldersRes.data);
      setProjects(projectsRes.data);
      setStats(statsRes.data);
      await loadCollaterals();
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadCollaterals = async () => {
    try {
      const params = {};
      if (selectedFolder) params.folder_id = selectedFolder;
      if (typeFilter !== 'all') params.type = typeFilter;
      if (projectFilter !== 'all') params.project_id = projectFilter;
      
      const res = await collateralAPI.list(params);
      setCollaterals(res.data);
    } catch (error) {
      console.error('Failed to load collaterals');
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolder.name.trim()) {
      toast.error('Folder name is required');
      return;
    }
    try {
      if (folderDialog.isEdit && folderDialog.folder) {
        await collateralAPI.updateFolder(folderDialog.folder.id, newFolder);
        toast.success('Folder updated');
      } else {
        await collateralAPI.createFolder(newFolder);
        toast.success('Folder created');
      }
      setFolderDialog({ open: false, folder: null, isEdit: false });
      setNewFolder({ name: '', description: '', color: '#3b82f6' });
      loadData();
    } catch (error) {
      toast.error('Failed to save folder');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!confirm('Delete this folder? Files will be moved to root.')) return;
    try {
      await collateralAPI.deleteFolder(folderId);
      toast.success('Folder deleted');
      if (selectedFolder === folderId) setSelectedFolder(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete folder');
    }
  };

  const handleCreateCollateral = async () => {
    if (!newCollateral.title.trim() || !newCollateral.url.trim()) {
      toast.error('Title and URL are required');
      return;
    }
    try {
      await collateralAPI.create({
        ...newCollateral,
        folder_id: selectedFolder || newCollateral.folder_id,
        project_id: newCollateral.project_id || null
      });
      toast.success('Collateral added');
      setUploadDialog(false);
      setNewCollateral({ title: '', file_type: 'document', url: '', thumbnail_url: '', project_id: '', folder_id: null, tags: [] });
      loadData();
    } catch (error) {
      toast.error('Failed to add collateral');
    }
  };

  const handleDeleteCollateral = async (id) => {
    if (!confirm('Delete this collateral?')) return;
    try {
      await collateralAPI.delete(id);
      toast.success('Collateral deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleMoveCollateral = async (collateralId, targetFolderId) => {
    try {
      await collateralAPI.move(collateralId, targetFolderId);
      toast.success('Collateral moved');
      setMoveDialog({ open: false, collateral: null });
      loadCollaterals();
    } catch (error) {
      toast.error('Failed to move');
    }
  };

  const getTypeIcon = (type) => {
    const iconMap = {
      'pdf': FileText,
      'document': FileText,
      'video': Video,
      'image': FileImage,
      'presentation': Presentation,
    };
    return iconMap[type] || File;
  };

  const getTypeColor = (type) => {
    const colorMap = {
      'pdf': 'bg-red-100 text-red-700',
      'document': 'bg-blue-100 text-blue-700',
      'video': 'bg-purple-100 text-purple-700',
      'image': 'bg-green-100 text-green-700',
      'presentation': 'bg-orange-100 text-orange-700',
    };
    return colorMap[type] || 'bg-slate-100 text-slate-700';
  };

  const filteredCollaterals = collaterals.filter(c => 
    c.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.tags?.some(t => t.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const folderColors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#ec4899', '#06b6d4'];

  if (loading) {
    return (
      <div className="space-y-6" data-testid="collaterals-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="collaterals-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Folder className="h-7 w-7 text-blue-600" />
            Collateral Hub
          </h1>
          <p className="text-muted-foreground">Manage and share marketing materials</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => {
              setNewFolder({ name: '', description: '', color: '#3b82f6' });
              setFolderDialog({ open: true, folder: null, isEdit: false });
            }} data-testid="create-folder-btn">
              <FolderPlus className="h-4 w-4 mr-2" />
              New Folder
            </Button>
            <Button onClick={() => setUploadDialog(true)} data-testid="upload-btn">
              <Upload className="h-4 w-4 mr-2" />
              Add Collateral
            </Button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Total Files</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold">{stats.folders}</p>
            <p className="text-xs text-muted-foreground">Folders</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-blue-600">{stats.by_type?.document || 0}</p>
            <p className="text-xs text-muted-foreground">Documents</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-green-600">{stats.by_type?.image || 0}</p>
            <p className="text-xs text-muted-foreground">Images</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-purple-600">{stats.by_type?.video || 0}</p>
            <p className="text-xs text-muted-foreground">Videos</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-red-600">{stats.by_type?.pdf || 0}</p>
            <p className="text-xs text-muted-foreground">PDFs</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="flex gap-6">
        {/* Folder Sidebar */}
        <div className="w-64 shrink-0">
          <Card className="sticky top-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Folders</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <ScrollArea className="h-[400px]">
                {/* All Files */}
                <button
                  onClick={() => setSelectedFolder(null)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                    selectedFolder === null ? 'bg-blue-100 text-blue-700' : 'hover:bg-slate-100'
                  }`}
                  data-testid="all-files-btn"
                >
                  <Folder className="h-4 w-4" />
                  All Files
                  <Badge variant="secondary" className="ml-auto text-xs">{stats.total}</Badge>
                </button>

                {/* Folders List */}
                {folders.map(folder => (
                  <div key={folder.id} className="group relative">
                    <button
                      onClick={() => setSelectedFolder(folder.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                        selectedFolder === folder.id ? 'bg-blue-100 text-blue-700' : 'hover:bg-slate-100'
                      }`}
                      data-testid={`folder-${folder.id}`}
                    >
                      <FolderOpen className="h-4 w-4" style={{ color: folder.color }} />
                      <span className="truncate flex-1">{folder.name}</span>
                    </button>
                    {canManage && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100"
                          >
                            <MoreVertical className="h-3 w-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => {
                            setNewFolder({ name: folder.name, description: folder.description || '', color: folder.color });
                            setFolderDialog({ open: true, folder, isEdit: true });
                          }}>
                            <Edit2 className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => handleDeleteFolder(folder.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                ))}

                {folders.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">No folders yet</p>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Files Area */}
        <div className="flex-1">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search collaterals..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-36" data-testid="type-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="document">Documents</SelectItem>
                <SelectItem value="pdf">PDFs</SelectItem>
                <SelectItem value="image">Images</SelectItem>
                <SelectItem value="video">Videos</SelectItem>
                <SelectItem value="presentation">Presentations</SelectItem>
              </SelectContent>
            </Select>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-44" data-testid="project-filter">
                <Building2 className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1 bg-muted p-1 rounded-lg">
              <Button 
                variant={viewMode === 'grid' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button 
                variant={viewMode === 'list' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <button onClick={() => setSelectedFolder(null)} className="hover:text-foreground">
              All Files
            </button>
            {selectedFolder && (
              <>
                <ChevronRight className="h-4 w-4" />
                <span className="text-foreground">
                  {folders.find(f => f.id === selectedFolder)?.name}
                </span>
              </>
            )}
          </div>

          {/* Files Grid/List */}
          {filteredCollaterals.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <File className="h-16 w-16 text-muted-foreground mb-4" />
                <p className="text-lg font-medium mb-2">No collaterals found</p>
                <p className="text-muted-foreground text-center mb-4">
                  {selectedFolder 
                    ? 'This folder is empty. Add some collaterals.' 
                    : 'Start by uploading marketing materials.'}
                </p>
                {canManage && (
                  <Button onClick={() => setUploadDialog(true)}>
                    <Upload className="h-4 w-4 mr-2" />
                    Add Collateral
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredCollaterals.map(collateral => {
                const TypeIcon = getTypeIcon(collateral.file_type || collateral.type);
                return (
                  <Card 
                    key={collateral.id} 
                    className="group cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setPreviewDialog({ open: true, collateral })}
                    data-testid={`collateral-${collateral.id}`}
                  >
                    <CardContent className="p-4">
                      {/* Thumbnail */}
                      <div className="aspect-video bg-slate-100 rounded-lg mb-3 flex items-center justify-center overflow-hidden">
                        {collateral.thumbnail_url ? (
                          <img src={collateral.thumbnail_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <TypeIcon className="h-12 w-12 text-slate-400" />
                        )}
                      </div>
                      {/* Info */}
                      <h3 className="font-medium text-sm truncate mb-1">{collateral.title}</h3>
                      <div className="flex items-center justify-between">
                        <Badge className={`text-xs ${getTypeColor(collateral.file_type || collateral.type)}`}>
                          {collateral.file_type || collateral.type}
                        </Badge>
                        {canManage && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                              <DropdownMenuItem onClick={() => window.open(collateral.url, '_blank')}>
                                <ExternalLink className="h-4 w-4 mr-2" />
                                Open
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setMoveDialog({ open: true, collateral })}>
                                <Move className="h-4 w-4 mr-2" />
                                Move to Folder
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-red-600"
                                onClick={() => handleDeleteCollateral(collateral.id)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="divide-y">
                  {filteredCollaterals.map(collateral => {
                    const TypeIcon = getTypeIcon(collateral.file_type || collateral.type);
                    return (
                      <div 
                        key={collateral.id}
                        className="flex items-center gap-4 p-4 hover:bg-slate-50 cursor-pointer"
                        onClick={() => setPreviewDialog({ open: true, collateral })}
                      >
                        <div className={`h-12 w-12 rounded-lg flex items-center justify-center ${getTypeColor(collateral.file_type || collateral.type)}`}>
                          <TypeIcon className="h-6 w-6" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium truncate">{collateral.title}</h3>
                          <p className="text-sm text-muted-foreground">
                            {collateral.file_type || collateral.type} 
                            {collateral.file_size && ` • ${(collateral.file_size / 1024 / 1024).toFixed(2)} MB`}
                          </p>
                        </div>
                        <Badge className={getTypeColor(collateral.file_type || collateral.type)}>
                          {collateral.file_type || collateral.type}
                        </Badge>
                        {canManage && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                              <DropdownMenuItem onClick={() => window.open(collateral.url, '_blank')}>
                                <ExternalLink className="h-4 w-4 mr-2" />
                                Open
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setMoveDialog({ open: true, collateral })}>
                                <Move className="h-4 w-4 mr-2" />
                                Move
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-red-600"
                                onClick={() => handleDeleteCollateral(collateral.id)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Add Collateral
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={newCollateral.title}
                onChange={(e) => setNewCollateral({ ...newCollateral, title: e.target.value })}
                placeholder="E.g., Project Brochure 2024"
                data-testid="collateral-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>File Type</Label>
              <Select 
                value={newCollateral.file_type} 
                onValueChange={(v) => setNewCollateral({ ...newCollateral, file_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="document">Document</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="image">Image</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                  <SelectItem value="presentation">Presentation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>File URL *</Label>
              <Input
                value={newCollateral.url}
                onChange={(e) => setNewCollateral({ ...newCollateral, url: e.target.value })}
                placeholder="https://drive.google.com/... or uploaded URL"
                data-testid="collateral-url-input"
              />
              <p className="text-xs text-muted-foreground">Paste a Google Drive, Dropbox, or direct file URL</p>
            </div>
            <div className="space-y-2">
              <Label>Thumbnail URL (optional)</Label>
              <Input
                value={newCollateral.thumbnail_url}
                onChange={(e) => setNewCollateral({ ...newCollateral, thumbnail_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Project</Label>
                <Select 
                  value={newCollateral.project_id} 
                  onValueChange={(v) => setNewCollateral({ ...newCollateral, project_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">General (No Project)</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Folder</Label>
                <Select 
                  value={newCollateral.folder_id || ''} 
                  onValueChange={(v) => setNewCollateral({ ...newCollateral, folder_id: v || null })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Root folder" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Root (No Folder)</SelectItem>
                    {folders.map(f => (
                      <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setUploadDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateCollateral} data-testid="save-collateral-btn">Add Collateral</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Folder Dialog */}
      <Dialog open={folderDialog.open} onOpenChange={(open) => setFolderDialog({ ...folderDialog, open })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderPlus className="h-5 w-5" />
              {folderDialog.isEdit ? 'Edit Folder' : 'New Folder'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Folder Name *</Label>
              <Input
                value={newFolder.name}
                onChange={(e) => setNewFolder({ ...newFolder, name: e.target.value })}
                placeholder="E.g., Brochures"
                data-testid="folder-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={newFolder.description}
                onChange={(e) => setNewFolder({ ...newFolder, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex gap-2">
                {folderColors.map(color => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-full border-2 ${newFolder.color === color ? 'border-black' : 'border-transparent'}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewFolder({ ...newFolder, color })}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setFolderDialog({ open: false, folder: null, isEdit: false })}>Cancel</Button>
            <Button onClick={handleCreateFolder} data-testid="save-folder-btn">
              {folderDialog.isEdit ? 'Update' : 'Create'} Folder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Dialog */}
      <Dialog open={moveDialog.open} onOpenChange={(open) => setMoveDialog({ ...moveDialog, open })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Move className="h-5 w-5" />
              Move to Folder
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-4">
            <p className="text-sm text-muted-foreground mb-4">
              Moving: <strong>{moveDialog.collateral?.title}</strong>
            </p>
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={() => handleMoveCollateral(moveDialog.collateral?.id, null)}
            >
              <Folder className="h-4 w-4 mr-2" />
              Root (No Folder)
            </Button>
            {folders.map(folder => (
              <Button
                key={folder.id}
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleMoveCollateral(moveDialog.collateral?.id, folder.id)}
              >
                <FolderOpen className="h-4 w-4 mr-2" style={{ color: folder.color }} />
                {folder.name}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialog.open} onOpenChange={(open) => setPreviewDialog({ ...previewDialog, open })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{previewDialog.collateral?.title}</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {previewDialog.collateral?.file_type === 'image' || previewDialog.collateral?.type === 'image' ? (
              <img 
                src={previewDialog.collateral?.url} 
                alt={previewDialog.collateral?.title}
                className="w-full rounded-lg"
              />
            ) : previewDialog.collateral?.file_type === 'video' || previewDialog.collateral?.type === 'video' ? (
              <video 
                src={previewDialog.collateral?.url}
                controls
                className="w-full rounded-lg"
              />
            ) : (
              <div className="text-center py-8">
                <File className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">Preview not available for this file type</p>
                <Button onClick={() => window.open(previewDialog.collateral?.url, '_blank')}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open File
                </Button>
              </div>
            )}
          </div>
          <div className="flex justify-between items-center mt-4 pt-4 border-t">
            <div className="flex items-center gap-2">
              <Badge className={getTypeColor(previewDialog.collateral?.file_type || previewDialog.collateral?.type)}>
                {previewDialog.collateral?.file_type || previewDialog.collateral?.type}
              </Badge>
              {previewDialog.collateral?.share_count > 0 && (
                <Badge variant="secondary">{previewDialog.collateral?.share_count} shares</Badge>
              )}
            </div>
            <Button onClick={() => window.open(previewDialog.collateral?.url, '_blank')}>
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Original
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CollateralsPage;
